import { createSearchParams } from 'react-router-dom'
import { getLocalStorageData } from './LocalStorageAccess'

export const humanizeBool = (bool) => (bool ? 'Yes' : 'No')
export const formatEmptyValue = (value) => (value ? value : '-')
export const formatDate = (value) => {
  return new Date(value).toDateString()
}

export const formatDateTime = (value) => {
  let dateObj = new Date(value)
  return `${dateObj.toDateString()} ${dateObj.getHours()}:${dateObj.getMinutes()} ${dateObj.getHours() < 12 ? 'AM' : 'PM'}`
}

/**
 * Fetches a file straight from its media URL and saves it.
 *
 * Resolves true when the file was saved, false otherwise. Callers must act on
 * the result: this does not go through services/base.js, so none of the
 * response-handling guards apply to it.
 *
 * `onProgress(percent)`, when given, follows the transfer: 0-100, or null when
 * the server does not say how large the file is.
 *
 * @returns {Promise<boolean>}
 */
export const downloadFile = (fileURL, fileName, onProgress) =>
  new Promise((resolve) => {
    if (!fileURL) {
      resolve(false)
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('get', fileURL, true)
    xhr.responseType = 'blob'
    if (onProgress) {
      xhr.onprogress = (event) => {
        onProgress(event.lengthComputable ? Math.floor((event.loaded / event.total) * 100) : null)
      }
    }
    xhr.onload = function () {
      // A 404 or 500 also fires onload with readyState 4. Without the status
      // check the error body was saved under the requested file name.
      if (xhr.status < 200 || xhr.status >= 300) {
        resolve(false)
        return
      }
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(xhr.response)
      link.download = fileName
      link.click()
      resolve(true)
    }
    // A network failure fires onerror, never onload, so this used to do nothing
    // at all - the link simply appeared inert.
    xhr.onerror = function () {
      resolve(false)
    }
    xhr.send()
  })

export const downloadErrorLogs = (text, filename = 'log.txt') => {
  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(text.join('\n'))
  )
  element.setAttribute('download', filename)

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

export const downloadFileBlob = (data, fileName) => {
  let url = window.URL.createObjectURL(data)
  let link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${fileName}`)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const toTitleCase = (str) => {
  return str
    .toLocaleLowerCase()
    .replace(
      /(^|Ü|ü|Ş|ş|Ç|ç|İ|ı|Ö|ö|\w)\S*/g,
      (txt) => txt.charAt(0).toLocaleUpperCase() + txt.substring(1)
    )
}

export const getVehicleRedirectUrl = () => {
  return getLocalStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getLocalStorageData('vehicle'))}`
    : `/vehicles`
}

export const getRandomString = () => {
  return Math.random().toString(36).slice(2, 30)
}

export const buildPageUrl = (apiUrl, page, pageSize = 10) => {
  const [path, query = ''] = apiUrl.split('?')
  const params = new URLSearchParams(query)
  const size = Number(pageSize) || 10
  params.set('limit', size)
  params.set('offset', (Math.max(1, Number(page) || 1) - 1) * size)
  return `${path}?${params.toString()}`
}

export const getDeleteTargetPage = (pagination, deletedCount = 1, pageSize = 10) => {
  const remaining = pagination.totalCount - deletedCount
  const newTotalPages = Math.max(1, Math.ceil(remaining / pageSize))
  return Math.min(pagination.currentPage, newTotalPages)
}
